import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface CountTile<K extends string> {
  key: K;
  label: string;
  count: number | null;
  accent?: string;
  /** Wraps the label to explain it on hover. */
  renderLabel?: (label: string) => ReactNode;
}

interface CountTilesProps<K extends string> {
  tiles: Array<CountTile<K>>;
  active: K;
  onChange: (key: K) => void;
}

/** Row of clickable count tiles used as a single-select filter. */
function CountTiles<K extends string>({ tiles, active, onChange }: CountTilesProps<K>) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {tiles.map(({ key, label, count, accent, renderLabel }) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(key)}
            className={cn(
              "rounded-lg border p-3 text-left transition-colors hover:bg-muted/50",
              isActive ? "border-primary bg-muted ring-2 ring-primary" : "border-border"
            )}
          >
            <div className="text-sm text-muted-foreground">
              {renderLabel ? renderLabel(label) : label}
            </div>
            <div className={cn("text-2xl font-semibold", accent ?? "text-foreground")}>
              {count ?? "—"}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default CountTiles;
